import { useEffect, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import SubmitButton from "../../Components/Button";
import { getLDAPDetails, updateDirectory, testLDAPConnection } from "../../api/common";
import TextInput from "../../Components/TextInput";
import Alert from "../../Components/AlertPopup";
import Breadcrumb from "../../Components/Breadcrumb";
import PageContainer from "../PageContainer";
import styles from "./style.module.scss";
import { encrypt, getSessionStorage } from "../../utils";
import {Spinner} from "react-bootstrap";
import Tick from "../../icons/tick.svg?react";

// Placeholder password shown in UI
const PASSWORD_PLACEHOLDER = "secret";

// Interface for API response data
interface LDAPApiData {
  id: number | null;
  managerDN: string;
  managerPassword: string;
  ip: string;
  port: number;
  baseDN: string;
  groupBaseDN: string;
  userFilter: string;
  secure: boolean;
}

const LDAPConfig = () => {
  const navigate = useNavigate();

  // Store original API data (for sending in payload)
  const [apiData, setApiData] = useState<LDAPApiData | null>(null);

  // Form field states
  const [managerDN, setManagerDN] = useState("");
  const [managerPassword, setManagerPassword] = useState(PASSWORD_PLACEHOLDER);
  const [ip, setIp] = useState("");
  const [port, setPort] = useState("");
  const [baseDN, setBaseDN] = useState("");
  const [groupBaseDN, setGroupBaseDN] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [secure, setSecure] = useState(false);

  // Loading states for buttons
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  // Cancel confirmation modal state
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Alert states
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showTestSuccessToast, setShowTestSuccessToast] = useState(false);
  const [showTestErrorToast, setShowTestErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Breadcrumb navigation items (with paths for display only - click disabled via CSS)
  const breadcrumbItems = [
    { label: "Configuration",path: "#" },
    { label: "Settings", path: "#" },
    { label: "LDAP Configuration" },
  ];

  // Fetch LDAP config from API on component mount
  useEffect(() => {
    // Reset all alert states when component mounts
    setShowSuccessToast(false);
    setShowErrorToast(false);
    setShowTestSuccessToast(false);
    setShowTestErrorToast(false);

    const loadInitialData = async () => {
      setFetchLoading(true);
      try {
        await fetchLDAPData();
      } catch {
        setShowErrorToast(true);
        setErrorMessage("Failed to load LDAP configuration");
      } finally {
        setFetchLoading(false);
      }
    };
    loadInitialData();
  }, []);

  // Populate form fields when API data loads
  useEffect(() => {
    if (apiData) {
      setManagerDN(apiData.managerDN);
      setManagerPassword(PASSWORD_PLACEHOLDER);
      setIp(apiData.ip);
      setPort(apiData.port?.toString() || "");
      setBaseDN(apiData.baseDN);
      setGroupBaseDN(apiData.groupBaseDN);
      setUserFilter(apiData.userFilter);
      setSecure(false);
    }
  }, [apiData]);

  // Handle checkbox change for Secure SSL
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSecure(e.target.checked);
  };

  // Build API payload from form state
  const buildPayload = () => {
    if (!apiData) return null;

    const encryptedPassword = encrypt(managerPassword);

    return {
      id: apiData.id,
      managerDN,
      managerPassword: encryptedPassword,
      ip,
      baseDN,
      port: Number(port),
      userFilter,
      groupBaseDN,
      secure
    };
  };

  // Handle Test Connection button click
  const handleTestConnection = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault(); // Prevent form submission
    if (!apiData) return;

    setTestLoading(true);

    try {
      const payload = buildPayload();
      console.log("Test Connection Payload:", payload);
      const response = await testLDAPConnection(payload);

      if (response?.statusCode === "OK" || response?.statusCodeValue === 200) {
        setShowTestSuccessToast(true);
      } else {
        setShowTestErrorToast(true);
        setErrorMessage(response?.body || "Connection test failed");
      }
    } catch (error) {
      setShowTestErrorToast(true);
      setErrorMessage(error instanceof Error ? error.message : "Failed to test LDAP connection");
    } finally {
      setTestLoading(false);
    }
  };

  // Check if form has been modified
  const hasChanges = () => {
    if (!apiData) return false;
    return secure !== apiData.secure;
  };

  // Handle Cancel button click - show confirmation modal only if there are changes
  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault(); // Prevent form submission
    if (hasChanges()) {
      // Show confirmation modal if there are unsaved changes
      setShowCancelModal(true);
    } else {
      // Navigate directly if no changes
      navigate("/overview/audit-overview");
    }
  };

  // Handle confirmation modal "Yes" click - redirect to audit overview
  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate("/overview/audit-overview");
  };

  // Handle confirmation modal "No" click - close modal
  const handleCloseModal = () => {
    setShowCancelModal(false);
  };


  // Fetch LDAP data from API
  const fetchLDAPData = async () => {
    try {
      const response = await getLDAPDetails();
      console.log("GET Directory Response:", response);
      if (response) {
        setApiData({
          id: response.id || null,
          managerDN: response.managerDN || "",
          managerPassword: response.managerPassword || "",
          ip: response.ip || "",
          port: response.port || 0,
          baseDN: response.baseDN || "",
          groupBaseDN: response.groupBaseDN || "",
          userFilter: response.userFilter || "",
          secure: response.secure || false,
        });
        setSecure(false); // Always default to unchecked
      }
    } catch (error) {
      console.error("Error fetching LDAP config:", error);
    }
  };

  // Handle Save button click
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiData) return;

    setLoading(true);

    try {
      const payload = buildPayload();
      const username = getSessionStorage("username") || "";
      const response = await updateDirectory(username, payload);

      if (response?.statusCode === "OK" || response?.statusCodeValue === 200) {
        // Call directory API to refresh data
        await fetchLDAPData();
        setShowSuccessToast(true);
      } else {
        setShowErrorToast(true);
        setErrorMessage(response?.body || "Failed to save configuration");
      }
    } catch (error) {
      setShowErrorToast(true);
      setErrorMessage(error instanceof Error ? error.message : "Failed to save LDAP configuration");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <PageContainer>
        <div className={styles.breadcrumbNoNav}>
        <Breadcrumb items={breadcrumbItems} />
      </div>
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}>Loading...</div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Breadcrumb Navigation */}
      <div className={styles.breadcrumbNoNav}>
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Form Container */}
      <div className={`${styles.formContainer} ${styles.inputField}`}>
        <Form onSubmit={handleSave} autoComplete="off">
          {/* Row 1: Manager DN & Password */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="managerDN">
                <Form.Label className={styles.fieldLabel}>
                  Manager DN<span className={styles.required}>*</span>
                </Form.Label>
                <TextInput
                  name="managerDN"
                  id="managerDN"
                  placeholder="Enter Manager DN"
                  value={managerDN}
                  onChange={(e) => setManagerDN(e.target.value)}
                  className={styles.inputField}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="managerPassword">
                <Form.Label className={styles.fieldLabel}>
                  Password<span className={styles.required}>*</span>
                </Form.Label>
                <TextInput
                  name="managerPassword"
                  id="managerPassword"
                  type="password"
                  placeholder="Enter Password"
                  value={managerPassword}
                  onChange={(e) => setManagerPassword(e.target.value)}
                  className={styles.inputField}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Row 2: Host & Port Number */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="ip">
                <Form.Label className={styles.fieldLabel}>
                  Host<span className={styles.required}>*</span>
                </Form.Label>
                <TextInput
                  name="ip"
                  id="ip"
                  placeholder="Enter Host"
                  value={ip}
                  onChange={(e) => setIp(e.target.value)}
                  className={styles.inputField}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="port">
                <Form.Label className={styles.fieldLabel}>
                  Port Number<span className={styles.required}>*</span>
                </Form.Label>
                <TextInput
                  name="port"
                  id="port"
                  placeholder="Enter Port Number"
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  className={styles.inputField}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Row 3: Base DN & Group Base DN */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="baseDN">
                <Form.Label className={styles.fieldLabel}>
                  Base DN<span className={styles.required}>*</span>
                </Form.Label>
                <TextInput
                  name="baseDN"
                  id="baseDN"
                  placeholder="Enter Base DN"
                  value={baseDN}
                  onChange={(e) => setBaseDN(e.target.value)}
                  className={styles.inputField}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="groupBaseDN">
                <Form.Label className={styles.fieldLabel}>
                  Group Base DN<span className={styles.required}>*</span>
                </Form.Label>
                <TextInput
                  name="groupBaseDN"
                  id="groupBaseDN"
                  placeholder="Enter Group Base DN"
                  value={groupBaseDN}
                  onChange={(e) => setGroupBaseDN(e.target.value)}
                  className={styles.inputField}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Row 4: User Filter & Secure SSL */}
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group controlId="userFilter">
                <Form.Label className={styles.fieldLabel}>
                  User Filter<span className={styles.required}>*</span>
                </Form.Label>
                <TextInput
                  name="userFilter"
                  id="userFilter"
                  placeholder="Enter User Filter"
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className={styles.inputField}
                />
              </Form.Group>
            </Col>
            <Col md={6} className={styles.checkboxCol}>
              <Form.Group controlId="secure">
                <Form.Check
                  type="checkbox"
                  label="Secure SSL"
                  checked={secure}
                  onChange={handleCheckboxChange}
                  className={styles.checkbox}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Action Buttons */}
          <div className={styles.buttonContainer}>
            <SubmitButton
              onClick={handleTestConnection}
              disabled={testLoading}
              className={styles.testButton}
            >
              Test Connection
              {testLoading && (
                <>
                  {" "}
                  <Spinner
                    animation="border"
                    variant="light"
                    className={styles.submitLoader}
                  />
                </>
              )}
  
            </SubmitButton>
            <SubmitButton
              type="secondary"
              onClick={handleCancel}
              disabled={loading}
              className={styles.cancelButton}
            >
              Cancel
            </SubmitButton>
            <SubmitButton
              onClick={handleSave}
              disabled={loading}
              className={styles.saveButton}
            >
              {loading ? "Saving..." : "Save"}
            </SubmitButton>
          </div>
        </Form>
      </div>

      {/* Success Alert for Save */}
      <Alert
        show={showSuccessToast}
        onClose={() => {
          setShowSuccessToast(false);
          navigate("/overview/audit-overview");
        }}
        message="LDAP configuration saved successfully!"
      />

      {/* Error Alert for Save */}
      <Alert
        type="error"
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        message={errorMessage || "Failed to save LDAP configuration"}
      />

      {/* Success Alert for Test Connection */}
      <Alert
        show={showTestSuccessToast}
        onClose={() => setShowTestSuccessToast(false)}
        message="Test Connection successfull!"
      />

      {/* Error Alert for Test Connection */}
      <Alert
        type="error"
        show={showTestErrorToast}
        onClose={() => setShowTestErrorToast(false)}
        message={errorMessage || "LDAP connection test failed"}
      />

      {/* Cancel Confirmation Modal */}
      <Modal show={showCancelModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Discard Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to cancel editing this entry?
          <br />
          Any unsaved changes will be lost.
        </Modal.Body>
        <Modal.Footer>
          <SubmitButton type="secondary" onClick={handleCloseModal}>
            No
          </SubmitButton>
          <SubmitButton onClick={handleConfirmCancel}>
            Yes
          </SubmitButton>
        </Modal.Footer>
      </Modal>
    </PageContainer>
  );
};

export default LDAPConfig;
